import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ColumnLayoutRowViewModel from '@/ViewModels/Controls/ColumnLayoutRowViewModel';
import ColumnLayoutItemViewModel from '@/ViewModels/Controls/ColumnLayoutItemViewModel';
import ColumnLayoutRow from '@/Controls/ColumnLayoutRow';

/**
 * Das ViewModel für die ColumnLayout
 */
export default class ColumnLayoutViewModel extends ControlViewModel {
    private ColumLayoutRowViewModel = ComponentManager.getViewModel<typeof ColumnLayoutRowViewModel>('ColumnLayoutRowViewModel');

    /**
     * Verwaltet ein internes Mapping für die Neighbor-Funktionalität
     */
    private rowTupleMapping: Object;

    /**
     * Die Factory-Tuples
     */
    public tuples: Array<any>;

    /**
     * Beinhaltet und verändert die Breite der LabelColumn
     */
    public labelColumnWidth: KnockoutObservable<number | string>;

    /**
     * Die Anzahl der Spalten pro Zeile
     */
    public columnsPerRow: number;

    /**
     * Die ColumnLayoutRows
     */
    public rows: KnockoutComputed<Array<ColumnLayoutRow>>;

    /**
     * Das ViewModel für die ColumnLayout
     * @param {object} params - Ein Objekt mit Optionen für das Control
     * @constructor
     */
    public constructor(params: any) {
        super(params);

        this.tuples = this.paramOrDefault(params.tuples, []);
        this.labelColumnWidth = this.observableOrDefault(params.labelColumnWidth, 200);
        this.columnsPerRow = this.paramOrDefault(params.columnsPerRow, 1);
        this.rows = this.defineComputed(() => {
            this.proofNeighborExist();
            this.rebuildNeighborMapping();

            let result = [];

            for (let row in this.rowTupleMapping) {
                result.push(new this.ColumLayoutRowViewModel({
                    tuples: this.rowTupleMapping[row],
                    labelColumnWidth: this.labelColumnWidth,
                    columnsPerRow: this.columnsPerRow
                }));
            }

            return result;
        });
    }

    private proofNeighborExist(): void {
        let tuples = this.tuples;
        let tuplesWithNeighbor = _.filter(tuples, (tuple: any) => {
            return tuple.factory.neighborOf != null;
        });

        _.each(tuplesWithNeighbor, (tupleWithNeighbor: any) => {
            let neighborOfTupel = _.find(tuples, (tuple: any) => {
                return tuple.factory.detailViewItem.name == tupleWithNeighbor.factory.neighborOf;
            });

            if (neighborOfTupel === undefined) {
                tupleWithNeighbor.factory.neighborOf = null;
            }
        });
    }

    private rebuildNeighborMapping(): void {
        let tuples = this.tuples;
        let columnsPerRow = this.columnsPerRow;

        this.rowTupleMapping = {};

        let tuplesWithoutNeighbor = _.filter(tuples, (tuple: any) => {
            return tuple.factory.neighborOf == null;
        });

        let tuplesWithNeighbor = _.filter(tuples, (tuple: any) => {
            return tuple.factory.neighborOf != null;
        });

        if (columnsPerRow > 1) {
            this.setNeighborForMappingForTuples(tuplesWithoutNeighbor);
            this.setNeighborForMappingForTuples(tuplesWithNeighbor);
        } else {
            this.setNeighborForMappingForTuples(tuples);
        }
    }

    private setNeighborForMappingForTuples(tuples: Array<any>) {
        let columnsPerRow = this.columnsPerRow;

        for (let i = 0; i < tuples.length; i++) {
            let tuple = tuples[i];
            let row = this.findRowForTuple(tuple);

            if (row != null) {
                let rowTuples = this.rowTupleMapping[row];
                if (rowTuples == null) {
                    rowTuples = [];
                    this.rowTupleMapping[row] = rowTuples;
                }

                if (rowTuples.length < columnsPerRow) {
                    if (columnsPerRow === 2 && rowTuples.length > 0 && tuple.factory.neighborOf != null) {
                        let neighbor = rowTuples[0];
                        if (neighbor.factory.colspan != 'One') {
                            // SCHLEUPENGRC-1496: Überschreibe Colspan-Eigenschaft der benachbarten Property, damit der Nachbar gesetzt werden kann.
                            neighbor.factory.colspan = 'One';
                        }
                    }
                    rowTuples.push(tuple);
                }
            }
        }
    }

    private findRowForTuple(tuple: any): any {
        let tuples = this.tuples;
        let factory = tuple.factory;
        let neighborOf = factory.neighborOf;
        let columnsPerRow = this.columnsPerRow;

        if (neighborOf == null || columnsPerRow === 1) {
            for (let row in this.rowTupleMapping) {
                if (this.rowTupleMapping[row].indexOf(tuple) > -1) {
                    return row;
                }
            }

            return _.keys(this.rowTupleMapping).length;
        } else {
            let neighbor = _.find(tuples, (iteratingTuple: any) => {
                return iteratingTuple.factory.detailViewItem.name === neighborOf;
            });

            if (neighbor != null) {
                return this.findRowForTuple(neighbor);
            } else {
                return null;
            }
        }
    }
}

ComponentManager.setViewModel('ColumnLayoutViewModel', ColumnLayoutViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');